/**
 * Comparison Utilities
 * Efficient comparison functions for arrays and objects
 */

/**
 * Compare two arrays for equality with optional custom comparator
 * More efficient than JSON.stringify for performance
 *
 * @param arr1 - First array to compare
 * @param arr2 - Second array to compare
 * @param itemComparator - Optional custom comparison function
 * @returns True if arrays are equal, false otherwise
 */
export function areArraysEqual<T>(
  arr1: T[],
  arr2: T[],
  itemComparator?: (a: T, b: T) => boolean
): boolean {
  if (arr1.length !== arr2.length) {
    return false;
  }

  const comparator =
    itemComparator ||
    ((a, b) => {
      // Basic deep equality for objects, shallow for primitives
      if (
        typeof a !== 'object' ||
        a === null ||
        typeof b !== 'object' ||
        b === null
      ) {
        return a === b;
      }
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);
      if (keysA.length !== keysB.length) {
        return false;
      }
      for (const key of keysA) {
        if (!keysB.includes(key) || (a as any)[key] !== (b as any)[key]) {
          return false;
        }
      }
      return true;
    });

  for (let i = 0; i < arr1.length; i++) {
    if (!comparator(arr1[i], arr2[i])) {
      return false;
    }
  }
  return true;
}
